import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  CheckCircle2,
  Compass,
  Dumbbell,
  Target,
  Trophy,
  Sparkles,
  MapPin,
  Star,
  ShieldCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { COACHES } from "@/lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/503c60d1-f640-4019-b359-c56b9e80ba98/images/f653428ccec0adbb3d6fd7ed62bad095b9ea82fddaae27d3060e6d40dca8a8c6.png";
const ACTION_IMG =
  "https://images.pexels.com/photos/10347865/pexels-photo-10347865.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PROGRAMMES = [
  {
    icon: Compass,
    title: "Position Specific",
    desc: "Win your role on the pitch. Tactical movement, decision-making and positional habits tuned to defenders, midfielders, wingers and strikers.",
    color: "blue",
  },
  {
    icon: Target,
    title: "Skill Specific",
    desc: "Pick a focus — first touch, finishing, 1v1s, passing range, dribbling — and we'll build a session that drills it under pressure.",
    color: "emerald",
  },
  {
    icon: Dumbbell,
    title: "Football Fitness",
    desc: "Sport-specific conditioning: acceleration, change of direction, repeat sprint capacity and the engine to play the full 90.",
    color: "orange",
  },
  {
    icon: Trophy,
    title: "U16-U18 Elite",
    desc: "Academy-level development with James — UEFA B qualified, England academy fitness coach. For players chasing the next level.",
    color: "violet",
  },
];

const FAQS = [
  {
    q: "Where do sessions take place?",
    a: "Our home base is Southampton Sports Centre. We also travel across Hythe and the wider Southampton area — at-home gardens, local parks, or premium Astro pitches (a small surface fee may apply for Astro).",
  },
  {
    q: "What ages do you coach?",
    a: "Jacob runs sessions for players aged 12-16 with a fun, technical and creativity-led approach. James focuses on U16-U18 elite development sessions for players chasing academy and senior level performance.",
  },
  {
    q: "How long is a session?",
    a: "Standard 1-1 sessions are 60 minutes. Football fitness blocks can run 45-75 minutes depending on the focus. We'll confirm timings when we accept your booking request.",
  },
  {
    q: "Do you offer block sessions?",
    a: "Yes — booking blocks of 5 or 10 sessions in advance unlocks a per-session discount. Choose your package on the booking form.",
  },
  {
    q: "How do I pay?",
    a: "We'll confirm your booking request by email or phone, share a payment link or bank details, and lock in your slot. No upfront payment is taken on this site.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden pitch-lines">
        <div className="hero-grid-bg absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 pt-16 md:pt-24 pb-20 md:pb-28 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <Badge
                data-testid="hero-badge"
                className="rounded-full bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 px-4 py-1.5 text-xs font-bold tracking-wide uppercase mb-6"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Development-heavy 1-1 coaching · Hythe & Southampton
              </Badge>
              <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter text-slate-950 leading-[0.95]">
                Better players.
                <br />
                <span className="text-blue-600">Built one session</span>
                <br />
                at a time.
              </h1>
              <p className="mt-7 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
                Position-specific, skill-specific and football fitness sessions for ages 12-18.
                Coached by{" "}
                <span className="font-semibold text-slate-900">Jacob Grant</span> — FA Level 1, UEFA C in progress and Division 1 player — and
                UEFA B qualified England academy fitness coach{" "}
                <span className="font-semibold text-slate-900">James Grant</span>.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link to="/book">
                  <Button
                    data-testid="hero-cta-book"
                    className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base font-bold shadow-soft hover:scale-[1.02] transition"
                  >
                    Request a Session
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#coaches">
                  <Button
                    data-testid="hero-cta-coaches"
                    variant="outline"
                    className="rounded-full border-slate-300 hover:bg-slate-100 text-slate-900 px-8 py-6 text-base font-bold"
                  >
                    Meet the Coaches
                  </Button>
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
                <div className="flex items-center gap-2 text-slate-700 font-semibold">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  UEFA B Qualified
                </div>
                <div className="flex items-center gap-2 text-slate-700 font-semibold">
                  <Trophy className="w-5 h-5 text-blue-600" />
                  England Academy Experience
                </div>
                <div className="flex items-center gap-2 text-slate-700 font-semibold">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  Hythe · Southampton
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 relative animate-pop-in">
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-soft bg-slate-100">
                <img
                  src={HERO_IMG}
                  alt="Football on bright green turf with stadium lights"
                  className="w-full h-[460px] object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent">
                  <div className="text-white">
                    <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-300">
                      Next intake
                    </div>
                    <div className="font-display text-2xl font-extrabold mt-1">
                      Weekday evenings & weekends
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-soft border border-slate-200 p-4 w-56">
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">From</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <div className="font-display text-4xl font-black text-slate-950">£20</div>
                  <div className="text-sm text-slate-600">/session</div>
                </div>
                <div className="text-xs text-slate-500 mt-1">Block discounts available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "12-18", l: "Player age range" },
            { k: "2", l: "Qualified coaches" },
            { k: "60 min", l: "Standard session" },
            { k: "3", l: "Session locations" },
          ].map((s) => (
            <div key={s.l} className="text-center md:text-left">
              <div className="font-display text-3xl md:text-4xl font-black text-slate-950 tracking-tighter">
                {s.k}
              </div>
              <div className="text-sm text-slate-600 mt-1 font-medium">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* COACHES */}
      <section id="coaches" data-testid="coaches-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-blue-600 mb-3">
              Your Coaches
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-950">
              Two coaches.
              <br />
              One development pathway.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Father and son. Different strengths, same standards. Pick the coach who matches your stage of development.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <CoachCard
              coach={COACHES.jacob}
              image={COACHES.jacob.image}
              accentBg="bg-emerald-500"
              accentText="text-emerald-700"
              accentLight="bg-emerald-50"
              accentBorder="border-emerald-200"
              testId="coach-card-jacob"
            />
            <CoachCard
              coach={COACHES.james}
              image={COACHES.james.image}
              accentBg="bg-blue-600"
              accentText="text-blue-700"
              accentLight="bg-blue-50"
              accentBorder="border-blue-200"
              testId="coach-card-james"
            />
          </div>
        </div>
      </section>

      {/* PROGRAMMES */}
      <section id="programmes" data-testid="programmes-section" className="py-20 md:py-28 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-600 mb-3">
                Programmes
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-950">
                Pick a focus.
                <br />
                We'll build the session.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-lg text-slate-600 leading-relaxed">
                Every session is built around a clear development goal. No generic warm-ups — every drill earns its place.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROGRAMMES.map((p, i) => (
              <ProgrammeCard key={p.title} {...p} index={i} />
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-12 gap-6 items-stretch">
            <div className="md:col-span-7 rounded-3xl overflow-hidden border border-slate-200">
              <img src={ACTION_IMG} alt="Coaching session" className="w-full h-72 md:h-80 object-cover" />
            </div>
            <Card className="md:col-span-5 rounded-3xl border-slate-200 bg-white p-8 flex flex-col justify-between">
              <div>
                <Sparkles className="w-7 h-7 text-blue-600 mb-3" />
                <h3 className="font-display text-2xl font-extrabold text-slate-950 tracking-tight">
                  Not sure which programme is right?
                </h3>
                <p className="text-slate-600 mt-3 leading-relaxed">
                  Tell us a bit about the player on the booking form — preferred foot, position, current level — and we'll recommend the best session type before confirming.
                </p>
              </div>
              <Link to="/book" className="mt-6">
                <Button
                  data-testid="programmes-cta"
                  className="w-full rounded-full bg-slate-950 hover:bg-slate-800 text-white py-6 font-bold"
                >
                  Start a booking request
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" data-testid="pricing-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-orange-600 mb-3">Pricing</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-950">
              Honest, simple pricing.
            </h2>
            <p className="mt-5 text-lg text-slate-600">
              Block bookings are slightly cheaper per session. Astro pitch hire may add a small surface fee.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <PriceCard
              coach="Jacob Grant"
              tag="Ages 12-16 · Fun, creative, technical"
              accent="emerald"
              prices={[
                { label: "Single 1-1 session", price: "£20", note: "60 minutes" },
                { label: "3 sessions bundle", price: "£50", note: "£16.67 per session · save £10" },
                { label: "Block of 5", price: "£90", note: "£18 per session · save £10" },
                { label: "Block of 10", price: "£170", note: "£17 per session · save £30" },
                { label: "2-player session", price: "£30", note: "£15 each · share a session with a mate" },
              ]}
              testId="price-card-jacob"
            />
            <PriceCard
              coach="James Grant"
              tag="UEFA B · U16-U18 elite & fitness"
              accent="blue"
              featured
              prices={[
                { label: "Single 1-1 session", price: "£50", note: "60 minutes" },
                { label: "Block of 5", price: "£225", note: "£45 per session · save £25" },
                { label: "Block of 10", price: "£425", note: "£42.50 per session · save £75" },
              ]}
              testId="price-card-james"
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                quote:
                  "My son left every session buzzing. Real technical work — not just rondos and a kick-around. His first touch is unrecognisable.",
                author: "Parent of a U14 winger",
              },
              {
                quote:
                  "James understands what it takes at academy level. The fitness sessions transformed how I felt in the last 20 minutes of games.",
                author: "U17 academy midfielder",
              },
              {
                quote:
                  "Jacob is brilliant with the younger players. Fun, encouraging, and you can see he genuinely loves coaching them.",
                author: "Parent of a U12 striker",
              },
            ].map((t) => (
              <div
                key={t.author}
                className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-emerald-400 text-emerald-400" />
                  ))}
                </div>
                <p className="text-lg text-white/90 leading-relaxed font-medium">
                  "{t.quote}"
                </p>
                <div className="mt-6 text-sm text-white/60 font-semibold">— {t.author}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-blue-600 mb-3">
              FAQ
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-950">
              Common questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`f-${i}`}
                data-testid={`faq-item-${i}`}
                className="border border-slate-200 rounded-2xl px-5 bg-white"
              >
                <AccordionTrigger className="font-display text-left text-lg font-bold text-slate-950 hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="rounded-3xl bg-blue-600 text-white p-10 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 hero-grid-bg opacity-20" />
            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <h3 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-tight">
                  Ready to put in the work?
                </h3>
                <p className="mt-4 text-white/90 text-lg max-w-xl">
                  Tell us a bit about the player and what they want to improve. We'll confirm your session within 24 hours.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link to="/book">
                  <Button
                    data-testid="footer-cta-book"
                    className="rounded-full bg-white text-blue-700 hover:bg-emerald-300 hover:text-slate-950 px-8 py-6 text-base font-bold transition"
                  >
                    Request a Session
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CoachCard({ coach, image, accentBg, accentText, accentLight, accentBorder, testId }) {
  return (
    <Card
      data-testid={testId}
      className="overflow-hidden rounded-3xl border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group"
    >
      <div className="relative aspect-[4/5] bg-slate-100 overflow-hidden">
        <img
          src={image}
          alt={coach.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className={`absolute top-4 left-4 ${accentBg} text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full`}>
          {coach.id === "james" ? "UEFA B" : "Qualified"}
        </div>
      </div>
      <div className="p-7">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight text-slate-950">
              {coach.name}
            </h3>
            <div className={`text-sm font-semibold ${accentText} mt-0.5`}>{coach.tagline}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs uppercase font-bold text-slate-500 tracking-wider">From</div>
            <div className="font-display text-3xl font-black text-slate-950 leading-none">£{coach.price}</div>
            <div className="text-[11px] text-slate-500">per session</div>
          </div>
        </div>

        <p className="text-slate-600 leading-relaxed">{coach.blurb}</p>

        <div className={`mt-5 inline-flex flex-wrap gap-2`}>
          {coach.badges.map((b) => (
            <span
              key={b}
              className={`text-xs font-bold ${accentLight} ${accentText} border ${accentBorder} px-3 py-1 rounded-full`}
            >
              {b}
            </span>
          ))}
        </div>

        <Link to={`/book?coach=${coach.id}`} className="block mt-6">
          <Button
            data-testid={`book-${coach.id}-btn`}
            className={`w-full rounded-full ${coach.id === "james" ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-500 hover:bg-emerald-600"} text-white py-6 font-bold`}
          >
            Book with {coach.name.split(" ")[0]}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </Card>
  );
}

function ProgrammeCard({ icon: Icon, title, desc, color, index }) {
  const colorMap = {
    blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
    orange: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
    violet: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
  };
  const c = colorMap[color];
  return (
    <Card
      data-testid={`programme-card-${index}`}
      className="rounded-3xl border border-slate-200 bg-white p-6 md:p-7 hover:-translate-y-1 hover:shadow-lg transition-all"
    >
      <div className={`w-12 h-12 rounded-2xl ${c.bg} ${c.text} grid place-items-center mb-5 border ${c.border}`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-display text-xl font-extrabold tracking-tight text-slate-950">{title}</h3>
      <p className="mt-3 text-slate-600 text-sm leading-relaxed">{desc}</p>
    </Card>
  );
}

function PriceCard({ coach, tag, prices, featured, accent, testId }) {
  const accents = {
    emerald: { dot: "bg-emerald-500", text: "text-emerald-700", chip: "bg-emerald-50 border-emerald-200 text-emerald-800" },
    blue: { dot: "bg-blue-600", text: "text-blue-700", chip: "bg-blue-50 border-blue-200 text-blue-800" },
  };
  const a = accents[accent];
  return (
    <Card
      data-testid={testId}
      className={`rounded-3xl p-8 md:p-10 border ${featured ? "border-blue-200 bg-white ring-1 ring-blue-100" : "border-slate-200 bg-white"} hover:shadow-lg transition`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight text-slate-950">
          {coach}
        </h3>
        {featured && (
          <span className="text-[11px] font-bold uppercase tracking-wider bg-blue-600 text-white px-3 py-1 rounded-full">
            Elite
          </span>
        )}
      </div>
      <div className={`text-sm font-semibold ${a.text}`}>{tag}</div>

      <div className="mt-6 space-y-3">
        {prices.map((p) => (
          <div
            key={p.label}
            className="flex items-center justify-between p-4 rounded-2xl border border-slate-200 bg-slate-50/50"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className={`w-5 h-5 mt-0.5 ${a.text}`} />
              <div>
                <div className="font-semibold text-slate-900">{p.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.note}</div>
              </div>
            </div>
            <div className="font-display text-xl font-black text-slate-950">{p.price}</div>
          </div>
        ))}
      </div>

      <Link to={`/book?coach=${coach.toLowerCase().split(" ")[0]}`} className="block mt-6">
        <Button
          data-testid={`${testId}-cta`}
          className={`w-full rounded-full ${featured ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-950 hover:bg-slate-800"} text-white py-6 font-bold`}
        >
          Book with {coach.split(" ")[0]}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </Card>
  );
}
